import { DOCUMENT_KIND_LABELS, ORDER_STATUSES, RETURN_STATUS_LABELS, cancelReasonText, type CustomerReturnView, type DocumentKind, type OrderDocumentInfo, type OrderStatus } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../api';
import { carrierLabel, customerText, fmt, statusLabel } from '../format';
import { useSession } from '../session';
import { trackingOf } from '../tracking';
import type { DocumentSettings, OrderSummary } from '../types';
import { ChannelBadge, Empty, ErrorBanner, Modal, SellerChip, Spinner, StatusChip, useLoad, useToast } from '../ui';
import { DocumentPreview, UploadDocumentDialog, type SellerDocumentKind } from '../ui/documents';
import { Icon } from '../ui/icons';
import { useSort } from '../ui/sort';
import { CarrierState, TrackingDetail, TrackingNumber } from '../ui/tracking';
import { AnnounceReturnDialog } from './seller/Returns';

const FILTERS: readonly { label: string; statuses: readonly OrderStatus[] | null }[] = [
  { label: 'Tümü', statuses: null },
  { label: 'Bekleyen', statuses: ['pending', 'backordered', 'on_hold'] },
  { label: 'Depoda', statuses: ['allocated', 'picking', 'picked', 'packing'] },
  { label: 'Kargoya hazır', statuses: ['packed'] },
  { label: 'Kargoda', statuses: ['shipped', 'delivered'] },
  { label: 'İptal / iade', statuses: ['cancelled', 'returned'] },
];

/** Until packing the seller can still attach the e-documents the parcel needs */
const OPEN: readonly OrderStatus[] = ['pending', 'backordered', 'on_hold', 'allocated', 'picking', 'picked', 'packing'];
/** Closed orders: their buyer's data can be erased (KVKK) */
const CLOSED: readonly OrderStatus[] = ['shipped', 'delivered', 'cancelled', 'returned'];
const SELLER_KINDS: readonly SellerDocumentKind[] = ['e_waybill', 'e_archive_invoice'];

interface OrderDetail extends OrderSummary {
  readonly items: readonly {
    readonly id: string;
    readonly sku: string | null;
    readonly name: string;
    readonly qtyOrdered: number;
    readonly qtyPicked: number;
    readonly qtyPacked: number;
    /** the marketplace cancelled the line while the rest of the order stood: it does not go with it */
    readonly channelCancelledAt?: string | null;
  }[];
}

/** Orders of every seller (staff) or of one's own shop (seller, with its e-documents). */
export function Orders() {
  const { isOperator, warehouseId, me } = useSession();
  // manual orders (Instagram, WhatsApp, B2B) are the seller's to enter
  const canCreate = !isOperator && me.role !== 'viewer';
  const [filter, setFilter] = useState(0);
  // links from other screens open the list on one order: #/siparisler?q=M0000012
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('q') ?? '');
  const [openId, setOpenId] = useState<string | null>(null);
  const statuses = FILTERS[filter]!.statuses;
  const params = new URLSearchParams({ limit: '300' });
  if (statuses) params.set('status', statuses.join(','));
  if (search.trim()) params.set('search', search.trim());
  if (isOperator && warehouseId) params.set('warehouseId', warehouseId);
  const query = params.toString();
  const list = useLoad(() => api<OrderSummary[]>('GET', `/api/orders?${query}`), [query], 20_000);
  const settings = useLoad(() => (isOperator ? Promise.resolve(null) : api<DocumentSettings>('GET', '/api/settings/documents')), [isOperator]);
  const required = settings.data?.requiredDocuments ?? [];
  /** how many "eksik" chips the order's e-Belge cell shows */
  const missing = (o: OrderSummary) =>
    OPEN.includes(o.status) ? SELLER_KINDS.filter((k) => (required.includes(k) || o.documents.some((d) => d.kind === k)) && !o.documents.some((d) => d.kind === k && d.status === 'ready')).length : 0;
  const sort = useSort<OrderSummary>('orders', {
    order: { label: 'Sipariş', value: (o) => o.orderNumber },
    ...(isOperator ? { seller: { label: 'Satıcı', value: (o) => o.tenantCode } } : {}),
    customer: { label: 'Müşteri', value: (o) => o.customerName },
    units: { label: 'Adet', value: (o) => o.units, desc: true },
    // the order's way through the warehouse, not the labels' alphabet
    status: { label: 'Durum', value: (o) => ORDER_STATUSES.indexOf(o.status) },
    // the orders still missing an e-document first
    ...(!isOperator ? { documents: { label: 'e-Belge', value: missing, desc: true } } : {}),
    tracking: { label: 'Takip no', value: (o) => trackingOf(o).number },
    date: { label: 'Tarih', value: (o) => o.orderedAt, desc: true },
  });
  const rows = sort.apply(list.data ?? []);

  /** e-İrsaliye chips for the seller: ready, or missing while the order can still take it */
  const documentChips = (o: OrderSummary) =>
    SELLER_KINDS.filter((k) => required.includes(k) || o.documents.some((d) => d.kind === k)).map((k) => {
      const doc = o.documents.find((d) => d.kind === k);
      if (doc?.status === 'ready')
        return (
          <span key={k} className="chip ok" style={{ marginRight: 4 }}>
            {DOCUMENT_KIND_LABELS[k]} ✓
          </span>
        );
      if (!OPEN.includes(o.status)) return null;
      return (
        <span key={k} className="chip warn" style={{ marginRight: 4 }}>
          {DOCUMENT_KIND_LABELS[k]} eksik
        </span>
      );
    });

  return (
    <>
      <div className="row">
        {FILTERS.map((f, i) => (
          <button key={f.label} className={`chip select${filter === i ? ' on' : ''}`} onClick={() => setFilter(i)}>
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {sort.picker}
        <input className="input" style={{ maxWidth: 260 }} placeholder="Sipariş no veya takip no" value={search} onChange={(e) => setSearch(e.target.value)} />
        {canCreate && (
          <>
            <a className="btn" href="#/siparisler/ice-aktar">
              <Icon name="upload" /> Excel ile yükle
            </a>
            <a className="btn primary" href="#/siparisler/yeni">
              <Icon name="plus" /> Yeni sipariş
            </a>
          </>
        )}
      </div>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : list.data.length === 0 ? (
          <Empty title="Sipariş yok" />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  {sort.th('order')}
                  {isOperator && sort.th('seller')}
                  {sort.th('customer', 'hide-sm')}
                  {sort.th('units', 'num')}
                  {sort.th('status')}
                  {!isOperator && sort.th('documents')}
                  {sort.th('tracking', 'hide-sm')}
                  {sort.th('date', 'hide-sm')}
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => setOpenId(o.id)}>
                    <td>
                      <span className="mono">{o.orderNumber}</span> <ChannelBadge channel={o.channel} />
                    </td>
                    {isOperator && (
                      <td>
                        <SellerChip code={o.tenantCode} />
                      </td>
                    )}
                    <td className="hide-sm">
                      <span className={o.customerName ? undefined : 'muted'}>{customerText(o)}</span>
                      {o.orderSource && <div className="muted" style={{ fontSize: 12 }}>{o.orderSource}</div>}
                    </td>
                    <td className="num" data-label="Adet">{o.units}</td>
                    <td>
                      <StatusChip status={o.status} />
                      {o.holdReason && <div className="muted" style={{ fontSize: 12 }}>{o.holdReason}</div>}
                      {o.status === 'cancelled' && o.cancelReason && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {cancelReasonText(o.cancelReason)}
                        </div>
                      )}
                      <CarrierState tracking={o.carrierTracking} />
                    </td>
                    {!isOperator && <td>{documentChips(o)}</td>}
                    <td className="hide-sm">
                      <TrackingNumber order={o} />
                      {o.carrierCode && <div className="muted" style={{ fontSize: 12 }}>{carrierLabel(o.carrierCode)}</div>}
                    </td>
                    <td className="nowrap hide-sm">{fmt.dateTime(o.orderedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {openId && <OrderDialog id={openId} onClose={() => setOpenId(null)} onChange={() => void list.reload()} />}
    </>
  );
}

function OrderDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { isOperator, isSupervisor, me } = useSession();
  const toast = useToast();
  const [erasing, setErasing] = useState(false);
  const order = useLoad(() => api<OrderDetail>('GET', `/api/orders/${id}`), [id]);
  const documents = useLoad(() => api<OrderDocumentInfo[]>('GET', `/api/orders/${id}/documents`), [id]);
  const [preview, setPreview] = useState<DocumentKind | null>(null);
  const [upload, setUpload] = useState<SellerDocumentKind | null>(null);
  const canUpload = !isOperator && me.role !== 'viewer' && order.data !== undefined && OPEN.includes(order.data.status);
  const returns = useLoad(() => api<CustomerReturnView[]>('GET', `/api/returns?orderId=${id}`), [id]);
  const [announcing, setAnnouncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // the depot stops working on it (a marketplace or the buyer cancelled it, or it left from elsewhere): the
  // seller's owner/manager or a warehouse supervisor/admin, while the parcel is still in the warehouse
  const canCancel = order.data !== undefined && !CLOSED.includes(order.data.status) && (isOperator ? isSupervisor : me.role !== 'viewer');
  const canAnnounce =
    !isOperator &&
    me.role !== 'viewer' &&
    order.data !== undefined &&
    (order.data.status === 'shipped' || order.data.status === 'delivered') &&
    !returns.data?.some((r) => r.status === 'expected');

  // KVKK: the buyer asked for their data to be deleted (it goes by itself once the order is closed long enough)
  const erase = async () => {
    if (!order.data) return;
    const question = `${order.data.orderNumber}: alıcının adı, iletişim bilgileri, adres satırları, fatura bilgisi ve etiket/e-belge içerikleri silinsin mi? Bu geri alınamaz; sipariş, ürünleri ve takip numarası kalır.`;
    if (!window.confirm(question)) return;
    setErasing(true);
    try {
      await api('POST', `/api/orders/${id}/erase-personal-data`);
      toast('Kişisel veriler silindi', 'ok');
      await Promise.all([order.reload(), documents.reload()]);
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setErasing(false);
    }
  };

  return (
    <Modal title={order.data ? order.data.orderNumber : 'Sipariş'} onClose={onClose} wide>
      {!order.data ? (
        <Spinner />
      ) : (
        <div className="stack">
          <div className="row">
            <StatusChip status={order.data.status} />
            <SellerChip code={order.data.tenantCode} />
            <ChannelBadge channel={order.data.channel} />
            <span className="muted">
              {statusLabel(order.data.status)} · {fmt.dateTime(order.data.orderedAt)}
            </span>
            {order.data.status === 'shipped' && order.data.channelShippedAt && <span className="chip ok">Pazaryeri kargoda gösteriyor</span>}
            {order.data.personalDataErasedAt && (
              <span className="chip" title="KVKK: alıcının adı, iletişim bilgileri, adres satırları ve belge içerikleri silindi; sipariş, ürünleri ve takip numarası duruyor">
                {customerText({ customerName: null, personalDataErasedAt: order.data.personalDataErasedAt })}
              </span>
            )}
            {isSupervisor && !order.data.personalDataErasedAt && CLOSED.includes(order.data.status) && (
              <>
                <span style={{ flex: 1 }} />
                <button className="btn sm ghost" disabled={erasing} onClick={() => void erase()} title="İlgili kişi başvurusu: alıcının kişisel verilerini şimdi siler">
                  Kişisel veriyi şimdi sil
                </button>
              </>
            )}
          </div>
          <table className="t">
            <thead>
              <tr>
                <th>Ürün</th>
                <th className="num">Sipariş</th>
                <th className="num">Toplanan</th>
                <th className="num">Paketlenen</th>
              </tr>
            </thead>
            <tbody>
              {order.data.items.map((i) => (
                <tr key={i.id}>
                  <td>
                    {i.name} <span className="mono muted">{i.sku ?? ''}</span>
                    {i.channelCancelledAt && (
                      <span className="chip bad nowrap" style={{ marginLeft: 6 }} title={`${fmt.dateTime(i.channelCancelledAt)}: gönderilmez`}>
                        Pazaryerinde iptal
                      </span>
                    )}
                  </td>
                  <td className="num">{i.qtyOrdered}</td>
                  <td className="num">{i.qtyPicked}</td>
                  <td className="num">{i.qtyPacked}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TrackingDetail order={order.data} staff={isOperator} />
          {((returns.data?.length ?? 0) > 0 || canAnnounce) && (
            <div>
              <h3 style={{ marginBottom: 8 }}>İadeler</h3>
              <div className="row">
                {returns.data?.map((r) => (
                  <span key={r.id} className={`chip ${r.status === 'received' ? 'ok' : r.status === 'expected' ? 'warn' : ''}`}>
                    {r.rmaNumber} · {RETURN_STATUS_LABELS[r.status]}
                    {r.status === 'received' && ` · ${r.sellableUnits} sağlam, ${r.blockedUnits} ayrıldı`}
                  </span>
                ))}
                {canAnnounce && (
                  <button className="btn sm" onClick={() => setAnnouncing(true)}>
                    <Icon name="undo" size={16} /> İade bildir
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <h3 style={{ marginBottom: 8 }}>Belgeler</h3>
            {!documents.data ? (
              <Spinner />
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                {documents.data.length === 0 && <span className="muted">Henüz belge yok.</span>}
                {documents.data.map((d) => (
                  <div key={d.id} className="row">
                    <b style={{ minWidth: 130 }}>{DOCUMENT_KIND_LABELS[d.kind]}</b>
                    <span className={`chip ${d.status === 'ready' ? 'ok' : d.status === 'failed' ? 'bad' : 'warn'}`}>
                      {d.status === 'ready' ? 'Hazır' : d.status === 'failed' ? 'Hata' : 'Bekleniyor'}
                    </span>
                    {d.documentNumber && <span className="mono muted">{d.documentNumber}</span>}
                    {d.error && <span className="muted">{d.error}</span>}
                    {d.contentErasedAt && <span className="muted">İçerik silindi ({fmt.date(d.contentErasedAt)}, KVKK)</span>}
                    <span style={{ flex: 1 }} />
                    {d.status === 'ready' && !d.contentErasedAt && (
                      <button className="btn sm" onClick={() => setPreview(d.kind)}>
                        <Icon name="eye" size={16} /> Önizle
                      </button>
                    )}
                  </div>
                ))}
                {canUpload && (
                  <div className="row">
                    {SELLER_KINDS.map((k) => (
                      <button key={k} className="btn sm" onClick={() => setUpload(k)}>
                        <Icon name="upload" size={16} /> {DOCUMENT_KIND_LABELS[k]} {documents.data!.some((d) => d.kind === k && d.status === 'ready') ? 'değiştir' : 'yükle'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {canCancel && (
            <div className="row">
              <span style={{ flex: 1 }} />
              <button className="btn sm danger-text" onClick={() => setCancelling(true)} title="Depo bu siparişi hazırlamaz; pazaryerindeki sipariş değişmez">
                Siparişi iptal et
              </button>
            </div>
          )}
        </div>
      )}
      {cancelling && order.data && (
        <CancelOrderDialog
          id={id}
          orderNumber={order.data.orderNumber}
          marketplace={order.data.channel !== 'manual'}
          onClose={() => setCancelling(false)}
          onDone={(picked) => {
            setCancelling(false);
            toast(
              picked
                ? `${order.data!.orderNumber} depoda iptal edildi. Toplanan ürünleri Yerleştirme → Rafa iade'den yerine koyun.`
                : `${order.data!.orderNumber} depoda iptal edildi; ayrılan stok serbest kaldı.`,
              'ok',
            );
            void order.reload();
            onChange();
          }}
        />
      )}
      {announcing && order.data && (
        <AnnounceReturnDialog
          order={{ id, orderNumber: order.data.orderNumber }}
          onClose={() => setAnnouncing(false)}
          onDone={(r) => {
            setAnnouncing(false);
            toast(`${r.rmaNumber} bildirildi; depo paketi iade kargo koduyla tanıyacak`, 'ok');
            void returns.reload();
          }}
        />
      )}
      {preview && order.data && <DocumentPreview orderId={id} orderNumber={order.data.orderNumber} kind={preview} onClose={() => setPreview(null)} />}
      {upload && order.data && (
        <UploadDocumentDialog
          orderId={id}
          orderNumber={order.data.orderNumber}
          kind={upload}
          onClose={() => setUpload(null)}
          onDone={() => {
            toast(`${DOCUMENT_KIND_LABELS[upload]} yüklendi`, 'ok');
            setUpload(null);
            void documents.reload();
            onChange();
          }}
        />
      )}
    </Modal>
  );
}

const CANCEL_REASONS = ['Müşteri iptal etti', 'Pazaryerinde iptal edildi', 'Depo dışından gönderildi'] as const;

/** "Siparişi iptal et": the warehouse stops; what was picked goes back to its place through Rafa iade. */
function CancelOrderDialog(props: { id: string; orderNumber: string; marketplace: boolean; onClose: () => void; onDone: (picked: boolean) => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const cancelled = await api<OrderDetail>('POST', `/api/orders/${props.id}/cancel`, { reason: reason.trim() });
      props.onDone(cancelled.items.some((i) => i.qtyPicked > 0 || i.qtyPacked > 0));
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${props.orderNumber} depoda iptal edilsin mi?`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn danger" disabled={busy || reason.trim().length < 3} onClick={() => void submit()}>
            Siparişi iptal et
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <p>
          Depo bu siparişi hazırlamaz: toplama rotasından çıkar, ayrılan stok serbest kalır. Toplanmış ya da paketlenmiş ürünler için Yerleştirme →
          Rafa iade görevi açılır; koli kapandıysa Sevkiyat “KARGOYA VERMEYİN” der.
        </p>
        {props.marketplace && (
          <p className="muted">Pazaryerindeki sipariş bundan etkilenmez. Sipariş pazaryerinde hâlâ açıksa onu da satıcı panelinden iptal edin.</p>
        )}
        <div className="field">
          <label htmlFor="cancel-reason">Gerekçe</label>
          <div className="row" style={{ marginBottom: 8 }}>
            {CANCEL_REASONS.map((r) => (
              <button key={r} type="button" className={`chip select${reason === r ? ' on' : ''}`} onClick={() => setReason(r)}>
                {r}
              </button>
            ))}
          </div>
          <textarea id="cancel-reason" className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Örn. müşteri iptal etti" />
        </div>
      </div>
    </Modal>
  );
}
